/**
 * Bank Rec Reports — pull Xero bank-transaction history into our DB, then
 * browse it month-by-month with total in/out + grouped-by-entity rollup.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowDownCircle, ArrowUpCircle, BadgeAlert, Banknote, CheckCircle2,
  ChevronLeft, ChevronRight, Download, Loader2, RefreshCw, Wallet,
} from "lucide-react";

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;
const FLOOR_DATE = "2026-01-01";
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtInt = (v) => (Number(v) || 0).toLocaleString("en-AU");
const fmtMoney = (v, ccy = "AUD") => {
  const n = Number(v) || 0;
  const sign = n < 0 ? "-" : "";
  return `${sign}${ccy === "AUD" ? "$" : ""}${Math.abs(n).toLocaleString("en-AU", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}${ccy && ccy !== "AUD" ? " " + ccy : ""}`;
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};
const fmtMonth = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-AU",
    { month: "long", year: "numeric" });
};
const shiftMonth = (ym, delta) => {
  if (!ym) return ym;
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/* ── Sync panel ─────────────────────────────────────────────── */
function SyncPanel({ config, accounts, activeAccount, onSynced }) {
  const [fromDate, setFromDate] = useState(FLOOR_DATE);
  const [toDate, setToDate] = useState(todayISO());
  const [scope, setScope] = useState("all"); // all | tenant | account
  const [forceRefetch, setForceRefetch] = useState(false);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (config?.last_synced_range?.to) {
      setFromDate(config.last_synced_range.to);
    }
  }, [config]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/bank-rec/sync");
      setHistory(data.jobs || []);
      const running = (data.jobs || []).find((j) => j.status === "running");
      if (running) setJob(running);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    if (!job || job.status !== "running") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/bank-rec/sync/${job.job_id}`);
        setJob(data);
        if (data.status !== "running") {
          if (data.status === "done") {
            toast.success(`Sync done · ${fmtInt(data.rows_fetched)} rows · ${fmtInt(data.inserted)} new, ${fmtInt(data.updated)} updated across ${data.targets_done}/${data.targets_total} account(s)`);
            onSynced && onSynced();
          } else {
            toast.error(`Sync failed: ${data.error || "Unknown"}`);
          }
          loadHistory();
        }
      } catch (e) { /* silent */ }
    }, 3000);
    return () => clearInterval(t);
  }, [job, onSynced, loadHistory]);

  const startSync = async () => {
    setBusy(true);
    try {
      const body = { from_date: fromDate, to_date: toDate, force_refetch: forceRefetch };
      if (scope === "tenant" && activeAccount) body.tenant_id = activeAccount.tenant_id;
      if (scope === "account" && activeAccount) {
        body.tenant_id = activeAccount.tenant_id;
        body.bank_account_id = activeAccount.account_id;
      }
      const { data } = await api.post("/bank-rec/sync", body);
      setJob({ ...data, rows_fetched: 0, inserted: 0, updated: 0,
        targets_total: 0, targets_done: 0 });
      toast.success("Sync started");
      loadHistory();
    } catch (e) { toast.error(errMsg(e, "Sync failed to start")); }
    finally { setBusy(false); }
  };

  const running = job?.status === "running";

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Download className="h-4 w-4 text-sky-600" />
          <div className="font-semibold text-sm">Sync bank transactions from Xero</div>
          {config?.last_synced_at && (
            <span className="text-[11px] text-zinc-500">
              Last sync: <b>{fmtDateTime(config.last_synced_at)}</b>
              {config.last_synced_range?.from && (
                <> ({config.last_synced_range.from} → {config.last_synced_range.to || "today"})</>
              )}
            </span>
          )}
        </div>
        <div className="text-[11px] text-zinc-500 -mt-1">
          Floor date: <b>{FLOOR_DATE}</b>. To pull older history, set From-date manually.
          Xero pages 100 rows at a time — expect ~1s per page per account.
        </div>

        <div className="grid md:grid-cols-5 gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-zinc-500">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              disabled={running} data-testid="bank-rec-sync-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-zinc-500">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              disabled={running} data-testid="bank-rec-sync-to" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-zinc-500">Scope</Label>
            <Select value={scope} onValueChange={setScope} disabled={running}>
              <SelectTrigger className="h-9 text-xs" data-testid="bank-rec-sync-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tenants + accounts</SelectItem>
                {activeAccount && (
                  <SelectItem value="tenant">
                    This tenant ({activeAccount.tenant_name})
                  </SelectItem>
                )}
                {activeAccount && (
                  <SelectItem value="account">
                    This account only ({activeAccount.name})
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-xs h-9 text-zinc-600">
            <input type="checkbox" checked={forceRefetch}
              onChange={(e) => setForceRefetch(e.target.checked)}
              disabled={running}
              data-testid="bank-rec-force-refetch" />
            Force overwrite
          </label>
          <Button onClick={startSync} disabled={running || busy || !accounts.length}
            data-testid="bank-rec-sync-btn">
            {(running || busy)
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <Download className="h-4 w-4 mr-1" />}
            {running ? "Syncing…" : "Sync now"}
          </Button>
        </div>

        {job && (
          <div className={`rounded border p-2.5 text-xs space-y-1
            ${job.status === "running" ? "bg-sky-50 border-sky-200"
              : job.status === "done" ? "bg-emerald-50 border-emerald-200"
              : "bg-red-50 border-red-200"}`}
            data-testid="bank-rec-sync-progress">
            <div className="flex items-center gap-2">
              {job.status === "running" && <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-600" />}
              {job.status === "done" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
              {job.status === "failed" && <BadgeAlert className="h-3.5 w-3.5 text-red-600" />}
              <span className="font-semibold capitalize">{job.status}</span>
              <span className="text-zinc-500">· {job.from_date} → {job.to_date || "today"}</span>
            </div>
            <div className="text-zinc-700">
              Accounts: <b>{fmtInt(job.targets_done)}/{fmtInt(job.targets_total)}</b> ·
              Rows: <b>{fmtInt(job.rows_fetched)}</b> ·
              New: <b>{fmtInt(job.inserted)}</b> ·
              Updated: <b>{fmtInt(job.updated)}</b>
              {job.current_account && (
                <span className="text-zinc-500 ml-2">
                  · {job.current_tenant} → {job.current_account}
                </span>
              )}
            </div>
            {job.error && <div className="text-red-700 break-words">{job.error}</div>}
            {job.errors?.length > 0 && (
              <details>
                <summary className="cursor-pointer text-red-700">
                  {job.errors.length} account error(s)
                </summary>
                <ul className="ml-5 list-disc text-red-700">
                  {job.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>{e.tenant_id?.slice(0, 8)}/{e.account_id?.slice(0, 8)}: {e.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {history.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-zinc-500 hover:text-zinc-800">
              Recent syncs ({history.length})
            </summary>
            <div className="mt-2 space-y-1">
              {history.slice(0, 10).map((h) => (
                <div key={h.job_id}
                  className="flex items-center gap-2 rounded border border-zinc-200 px-2 py-1.5 bg-white">
                  <Badge variant="outline" className={`text-[10px]
                    ${h.status === "done" ? "border-emerald-200 text-emerald-700 bg-emerald-50"
                      : h.status === "failed" ? "border-red-200 text-red-700 bg-red-50"
                      : "border-sky-200 text-sky-700 bg-sky-50"}`}>{h.status}</Badge>
                  <span className="text-zinc-700">{h.from_date} → {h.to_date || "today"}</span>
                  <span className="text-zinc-500 ml-auto">
                    {fmtInt(h.rows_fetched)} rows · {fmtDateTime(h.created_at)}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Main page ───────────────────────────────────────────────── */
// Period modes: single month, calendar year, or Australian financial
// year (Jul→Jun). The picker at the top of the page switches between
// them and drives the summary + transactions requests.
const PERIOD_MODES = {
  month: "Month",
  calendar: "Calendar year",
  fy: "Financial year (Jul-Jun)",
};

function currentCalendarYear() {
  return new Date().getFullYear();
}
function currentFinancialYear() {
  // Australian FY ends 30 Jun. e.g. Feb 2026 is inside FY2026 (Jul 2025 → Jun 2026).
  const d = new Date();
  return d.getMonth() >= 6 ? d.getFullYear() + 1 : d.getFullYear();
}
function calendarRange(year) {
  return { from_month: `${year}-01`, to_month: `${year}-12` };
}
function financialYearRange(fyEndingYear) {
  // FY 2026 = Jul 2025 → Jun 2026
  const start = fyEndingYear - 1;
  return { from_month: `${start}-07`, to_month: `${fyEndingYear}-06` };
}

export default function BankRecReportsPage() {
  const [config, setConfig] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountsErrors, setAccountsErrors] = useState([]);
  const [activeAccountKey, setActiveAccountKey] = useState("");
  const [ym, setYm] = useState(currentMonth());
  const [periodMode, setPeriodMode] = useState("month");
  const [calYear, setCalYear] = useState(currentCalendarYear());
  const [fyYear, setFyYear] = useState(currentFinancialYear());
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [txnPage, setTxnPage] = useState(1);
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnTotalPages, setTxnTotalPages] = useState(1);
  const [txnLoading, setTxnLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(true);

  // Compute the actual params to send based on the selected period mode.
  const periodParams = useMemo(() => {
    if (periodMode === "calendar") return calendarRange(calYear);
    if (periodMode === "fy") return financialYearRange(fyYear);
    return { year_month: ym };
  }, [periodMode, calYear, fyYear, ym]);

  const periodLabel = useMemo(() => {
    if (periodMode === "calendar") return `Calendar ${calYear}`;
    if (periodMode === "fy") return `FY ${fyYear} (Jul ${fyYear - 1} – Jun ${fyYear})`;
    return fmtMonth(ym);
  }, [periodMode, calYear, fyYear, ym]);

  const activeAccount = useMemo(() =>
    accounts.find((a) => `${a.tenant_id}::${a.account_id}` === activeAccountKey) || null,
    [accounts, activeAccountKey]);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/bank-rec/config");
      setConfig(data);
    } catch (e) { /* silent */ }
  }, []);

  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const { data } = await api.get("/bank-rec/accounts");
      setAccounts(data.accounts || []);
      setAccountsErrors(data.errors || []);
      if (!activeAccountKey && (data.accounts || []).length > 0) {
        const first = data.accounts[0];
        setActiveAccountKey(`${first.tenant_id}::${first.account_id}`);
      }
    } catch (e) { toast.error(errMsg(e, "Failed to load bank accounts")); }
    finally { setAccountsLoading(false); }
  }, [activeAccountKey]);

  const loadSummary = useCallback(async () => {
    if (!activeAccount) { setSummary(null); return; }
    setSummaryLoading(true);
    try {
      const { data } = await api.get("/bank-rec/summary", {
        params: {
          tenant_id: activeAccount.tenant_id,
          bank_account_id: activeAccount.account_id,
          ...periodParams,
        },
      });
      setSummary(data);
    } catch (e) { toast.error(errMsg(e, "Failed to load summary")); }
    finally { setSummaryLoading(false); }
  }, [activeAccount, periodParams]);

  const loadTxns = useCallback(async () => {
    if (!activeAccount) { setTxns([]); setTxnTotal(0); return; }
    setTxnLoading(true);
    try {
      const { data } = await api.get("/bank-rec/transactions", {
        params: {
          tenant_id: activeAccount.tenant_id,
          bank_account_id: activeAccount.account_id,
          ...periodParams,
          page: txnPage,
          per_page: 100,
        },
      });
      setTxns(data.rows || []);
      setTxnTotal(data.total || 0);
      setTxnTotalPages(data.total_pages || 1);
    } catch (e) { toast.error(errMsg(e, "Failed to load transactions")); }
    finally { setTxnLoading(false); }
  }, [activeAccount, periodParams, txnPage]);

  useEffect(() => { loadConfig(); loadAccounts(); }, [loadConfig, loadAccounts]);
  useEffect(() => { loadSummary(); loadTxns(); }, [loadSummary, loadTxns]);
  useEffect(() => { setTxnPage(1); }, [activeAccountKey, ym, periodMode, calYear, fyYear]);

  const onSynced = useCallback(() => {
    loadConfig(); loadAccounts(); loadSummary(); loadTxns();
  }, [loadConfig, loadAccounts, loadSummary, loadTxns]);

  return (
    <div className="p-6 space-y-4 max-w-[1400px]" data-testid="bank-rec-page">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-sky-600" />Bank Rec Reports
          </h1>
          <div className="text-sm text-zinc-500 mt-1">
            Bank transaction history per Xero organisation and bank account, with
            monthly totals and entity-grouped rollups.
          </div>
        </div>
      </div>

      <SyncPanel config={config} accounts={accounts}
        activeAccount={activeAccount} onSynced={onSynced} />

      {accountsErrors.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs"
          data-testid="bank-rec-accounts-errors">
          <div className="font-semibold text-amber-900 mb-1">
            {accountsErrors.length} tenant(s) failed to load
          </div>
          <ul className="ml-5 list-disc text-amber-800">
            {accountsErrors.map((e, i) => (
              <li key={i}><b>{e.tenant_name || e.tenant_id}:</b> {e.error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Account picker */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold">Bank account:</div>
            {accountsLoading ? (
              <span className="text-xs text-zinc-500 flex items-center gap-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />Loading…
              </span>
            ) : accounts.length === 0 ? (
              <span className="text-xs text-zinc-500">No BANK-type accounts found in Xero.</span>
            ) : (
              <Select value={activeAccountKey} onValueChange={setActiveAccountKey}>
                <SelectTrigger className="h-9 text-xs w-full max-w-md"
                  data-testid="bank-rec-account-select">
                  <SelectValue placeholder="Select an account…" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={`${a.tenant_id}::${a.account_id}`}
                      value={`${a.tenant_id}::${a.account_id}`}>
                      <span className="font-medium">{a.name}</span>
                      <span className="text-zinc-400"> · {a.tenant_name}</span>
                      <span className="text-zinc-400 text-[10px] ml-1">
                        · {fmtInt(a.stored_rows)} rows stored
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button variant="ghost" size="sm" onClick={loadAccounts}
              disabled={accountsLoading} data-testid="bank-rec-reload-accounts">
              <RefreshCw className={`h-3.5 w-3.5 ${accountsLoading ? "animate-spin" : ""}`} />
            </Button>
            {activeAccount && (
              <div className="ml-auto flex items-center gap-2 text-xs flex-wrap"
                data-testid="bank-rec-period-picker">
                <Select value={periodMode} onValueChange={setPeriodMode}>
                  <SelectTrigger className="h-8 w-40 text-xs"
                    data-testid="bank-rec-period-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PERIOD_MODES).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {periodMode === "month" && (
                  <>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                      onClick={() => setYm(shiftMonth(ym, -1))}
                      data-testid="bank-rec-prev-month">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="font-semibold text-sm min-w-[140px] text-center">
                      {fmtMonth(ym)}
                    </div>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                      onClick={() => setYm(shiftMonth(ym, 1))}
                      data-testid="bank-rec-next-month">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Input type="month" value={ym}
                      onChange={(e) => e.target.value && setYm(e.target.value)}
                      className="h-8 w-40 text-xs"
                      data-testid="bank-rec-month-input" />
                  </>
                )}
                {periodMode === "calendar" && (
                  <>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                      onClick={() => setCalYear((y) => y - 1)}
                      data-testid="bank-rec-prev-cal-year">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="font-semibold text-sm min-w-[120px] text-center"
                      data-testid="bank-rec-cal-year-label">
                      {periodLabel}
                    </div>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                      onClick={() => setCalYear((y) => y + 1)}
                      data-testid="bank-rec-next-cal-year">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </>
                )}
                {periodMode === "fy" && (
                  <>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                      onClick={() => setFyYear((y) => y - 1)}
                      data-testid="bank-rec-prev-fy">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="font-semibold text-sm min-w-[220px] text-center"
                      data-testid="bank-rec-fy-label">
                      {periodLabel}
                    </div>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                      onClick={() => setFyYear((y) => y + 1)}
                      data-testid="bank-rec-next-fy">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Totals */}
      {activeAccount && (
        <div className="grid md:grid-cols-3 gap-3" data-testid="bank-rec-totals">
          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardContent className="p-4 flex items-center gap-3">
              <ArrowDownCircle className="h-8 w-8 text-emerald-600" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wide text-emerald-700">Total in</div>
                <div className="text-2xl font-bold text-emerald-800 tabular-nums">
                  {summaryLoading
                    ? <Loader2 className="h-5 w-5 animate-spin inline" />
                    : fmtMoney(summary?.totals?.in?.total || 0, activeAccount.currency)}
                </div>
                <div className="text-[11px] text-emerald-700">
                  {fmtInt(summary?.totals?.in?.count || 0)} transaction(s)
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/40">
            <CardContent className="p-4 flex items-center gap-3">
              <ArrowUpCircle className="h-8 w-8 text-red-600" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wide text-red-700">Total out</div>
                <div className="text-2xl font-bold text-red-800 tabular-nums">
                  {summaryLoading
                    ? <Loader2 className="h-5 w-5 animate-spin inline" />
                    : fmtMoney(summary?.totals?.out?.total || 0, activeAccount.currency)}
                </div>
                <div className="text-[11px] text-red-700">
                  {fmtInt(summary?.totals?.out?.count || 0)} transaction(s)
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`${(summary?.totals?.net || 0) >= 0
              ? "border-sky-200 bg-sky-50/40" : "border-amber-200 bg-amber-50/40"}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <Banknote className={`h-8 w-8 ${(summary?.totals?.net || 0) >= 0 ? "text-sky-600" : "text-amber-600"}`} />
              <div className="flex-1">
                <div className={`text-[10px] uppercase tracking-wide
                  ${(summary?.totals?.net || 0) >= 0 ? "text-sky-700" : "text-amber-700"}`}>
                  Net (in − out)
                </div>
                <div className={`text-2xl font-bold tabular-nums
                  ${(summary?.totals?.net || 0) >= 0 ? "text-sky-800" : "text-amber-800"}`}>
                  {summaryLoading
                    ? <Loader2 className="h-5 w-5 animate-spin inline" />
                    : fmtMoney(summary?.totals?.net || 0, activeAccount.currency)}
                </div>
                <div className={`text-[11px]
                  ${(summary?.totals?.net || 0) >= 0 ? "text-sky-700" : "text-amber-700"}`}>
                  {(summary?.totals?.net || 0) >= 0 ? "Net inflow" : "Net outflow"}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Grouped rollup */}
      {activeAccount && (
        <div className="grid md:grid-cols-2 gap-3">
          <GroupCard title="Money in — grouped by entity" tone="in"
            groups={summary?.groups_in || []} loading={summaryLoading}
            currency={activeAccount.currency} />
          <GroupCard title="Money out — grouped by entity" tone="out"
            groups={summary?.groups_out || []} loading={summaryLoading}
            currency={activeAccount.currency} />
        </div>
      )}

      {/* Transactions table */}
      {activeAccount && (
        <Card>
          <CardContent className="p-0">
            <div className="p-3 border-b bg-zinc-50/60 flex items-center gap-2 text-sm">
              <div className="font-semibold">All transactions · {fmtMonth(ym)}</div>
              <Badge variant="outline" className="text-[10px]">{fmtInt(txnTotal)} row(s)</Badge>
              <Button variant="ghost" size="sm" onClick={loadTxns} disabled={txnLoading}
                className="ml-auto h-7" data-testid="bank-rec-reload-txns">
                <RefreshCw className={`h-3.5 w-3.5 ${txnLoading ? "animate-spin" : ""}`} />
              </Button>
            </div>
            {txnLoading && txns.length === 0 ? (
              <div className="p-8 text-sm text-zinc-500 text-center">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…
              </div>
            ) : txns.length === 0 ? (
              <div className="p-8 text-sm text-zinc-500 text-center" data-testid="bank-rec-empty">
                No transactions stored for this account in {fmtMonth(ym)}.<br />
                Click <b>Sync now</b> above to import from Xero.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bank-rec-txns-table">
                  <thead className="border-b bg-zinc-50">
                    <tr className="text-left text-[10px] uppercase tracking-wide text-zinc-500">
                      <th className="py-2 px-3 w-[100px]">Date</th>
                      <th className="py-2 px-3">Description</th>
                      <th className="py-2 px-3 w-[110px]">Type</th>
                      <th className="py-2 px-3 w-[120px]">Reference</th>
                      <th className="py-2 px-3 w-[80px]">Status</th>
                      <th className="py-2 px-3 w-[120px] text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((t) => (
                      <tr key={t.transaction_id}
                        className="border-b last:border-b-0 hover:bg-zinc-50/50"
                        data-testid={`bank-rec-row-${t.transaction_id}`}>
                        <td className="py-1.5 px-3 whitespace-nowrap tabular-nums">{t.date}</td>
                        <td className="py-1.5 px-3">
                          <div className="font-medium truncate max-w-[400px]">{t.description}</div>
                          {t.contact_name && t.contact_name !== t.description && (
                            <div className="text-[10px] text-zinc-500 truncate">{t.contact_name}</div>
                          )}
                        </td>
                        <td className="py-1.5 px-3 text-[11px] text-zinc-500">{t.type}</td>
                        <td className="py-1.5 px-3 text-[11px] font-mono text-zinc-500 truncate max-w-[120px]">
                          {t.reference || "—"}
                        </td>
                        <td className="py-1.5 px-3">
                          {t.is_reconciled
                            ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" variant="outline">reconciled</Badge>
                            : <Badge variant="outline" className="text-[10px]">{t.status || "—"}</Badge>}
                        </td>
                        <td className={`py-1.5 px-3 text-right font-semibold tabular-nums
                          ${t.direction === "in" ? "text-emerald-700" : "text-red-700"}`}>
                          {t.direction === "in" ? "+" : "−"}{fmtMoney(t.amount, t.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {txnTotal > 100 && (
              <div className="flex items-center justify-between p-3 border-t bg-zinc-50/60 text-xs">
                <div className="text-zinc-500">
                  Showing <b>{fmtInt((txnPage - 1) * 100 + 1)}</b>–
                  <b>{fmtInt(Math.min(txnPage * 100, txnTotal))}</b> of <b>{fmtInt(txnTotal)}</b>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                    disabled={txnPage <= 1} onClick={() => setTxnPage((p) => Math.max(1, p - 1))}
                    data-testid="bank-rec-txns-prev">
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <span className="px-2 tabular-nums">Page <b>{txnPage}</b> / {txnTotalPages}</span>
                  <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                    disabled={txnPage >= txnTotalPages}
                    onClick={() => setTxnPage((p) => Math.min(txnTotalPages, p + 1))}
                    data-testid="bank-rec-txns-next">
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ── Grouped rollup card ─────────────────────────────────────── */
function GroupCard({ title, tone, groups, loading, currency }) {
  const isIn = tone === "in";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {isIn
            ? <ArrowDownCircle className="h-4 w-4 text-emerald-600" />
            : <ArrowUpCircle className="h-4 w-4 text-red-600" />}
          <div className="font-semibold text-sm">{title}</div>
          <Badge variant="outline" className="text-[10px] ml-auto">
            {groups.length} entities
          </Badge>
        </div>
        {loading ? (
          <div className="text-xs text-zinc-500 py-4 text-center">
            <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-2" />Loading…
          </div>
        ) : groups.length === 0 ? (
          <div className="text-xs text-zinc-500 py-6 text-center">No transactions.</div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm" data-testid={`bank-rec-groups-${tone}`}>
              <thead className="border-b sticky top-0 bg-white">
                <tr className="text-left text-[10px] uppercase tracking-wide text-zinc-500">
                  <th className="py-1.5 px-2">Entity</th>
                  <th className="py-1.5 px-2 w-[50px] text-right">#</th>
                  <th className="py-1.5 px-2 w-[110px] text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={`${tone}::${g.group_key}::${g.label}`}
                    className="border-b last:border-b-0"
                    data-testid={`bank-rec-group-${tone}-${g.group_key}`}>
                    <td className="py-1 px-2 truncate max-w-[300px]" title={g.label}>
                      {g.label}
                    </td>
                    <td className="py-1 px-2 text-right text-zinc-500 tabular-nums">
                      {fmtInt(g.count)}
                    </td>
                    <td className={`py-1 px-2 text-right font-semibold tabular-nums
                      ${isIn ? "text-emerald-700" : "text-red-700"}`}>
                      {fmtMoney(g.total, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
